import { Injectable } from "@angular/core";
import { KeycloakService } from "keycloak-angular";

const SSO_LOGIN_ERROR_KEY = "sso-login-error";

export function getSsoAccessTokenFromHash(): string | null {
  const hash = window.location.hash;
  if (!hash) return null;

  const accessToken = new URLSearchParams(hash.slice(1)).get("access_token");

  if (accessToken) {
    window.history.replaceState(
      null,
      "",
      window.location.pathname + window.location.search
    );
  }

  return accessToken;
}

export function isValidUnexpiredJwt(token: string): boolean {
  const parts = token.split(".");
  if (parts.length !== 3 || parts.some((part) => !part)) return false;

  try {
    const header = JSON.parse(decodeBase64Url(parts[0]));
    const payload = getJwtPayload(token);

    return (
      typeof header === "object" &&
      header !== null &&
      typeof payload?.exp === "number" &&
      Number.isFinite(payload.exp) &&
      payload.exp > Date.now() / 1000
    );
  } catch {
    return false;
  }
}

function getJwtPayload(token: string): Record<string, unknown> {
  return JSON.parse(decodeBase64Url(token.split(".")[1]));
}

function decodeBase64Url(value: string): string {
  const base64 = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
  return decodeURIComponent(
    Array.from(atob(padded))
      .map((char) => `%${char.charCodeAt(0).toString(16).padStart(2, "0")}`)
      .join("")
  );
}

@Injectable({ providedIn: "root" })
export class SsoLoginService {
  constructor(private keycloakService: KeycloakService) {}

  async redirectToTraditionalLogin(): Promise<void> {
    sessionStorage.removeItem(SSO_LOGIN_ERROR_KEY);
    await this.keycloakService.login({
      redirectUri: `${window.location.origin}/`
    });
  }

  hasFailureMessage(): boolean {
    const hasFailure = sessionStorage.getItem(SSO_LOGIN_ERROR_KEY) === "true";
    sessionStorage.removeItem(SSO_LOGIN_ERROR_KEY);
    return hasFailure;
  }
}

function clearKeycloakSession(keycloak: KeycloakService): void {
  try {
    keycloak.clearToken();
  } catch {
    // The adapter has not been initialized yet, so there is no in-memory token.
  }
}

function storeAccessTokenInKeycloak(
  keycloak: KeycloakService,
  accessToken: string
): void {
  const tokenParsed = getJwtPayload(accessToken);
  const keycloakInstance = keycloak.getKeycloakInstance();

  keycloakInstance.token = accessToken;
  keycloakInstance.tokenParsed = tokenParsed as any;
  keycloakInstance.authenticated = true;
  keycloakInstance.subject = tokenParsed["sub"] as string;
  keycloakInstance.realmAccess = tokenParsed["realm_access"] as any;
  keycloakInstance.resourceAccess = tokenParsed["resource_access"] as any;
}

export async function initializeSsoAwareKeycloak(
  keycloak: KeycloakService,
  init: () => Promise<boolean>
): Promise<boolean> {
  const isSsoRoute = window.location.pathname === "/login";
  const accessToken = getSsoAccessTokenFromHash();

  if (!accessToken) {
    if (isSsoRoute) sessionStorage.setItem(SSO_LOGIN_ERROR_KEY, "true");
    return init();
  }

  if (!isValidUnexpiredJwt(accessToken)) {
    clearKeycloakSession(keycloak);
    sessionStorage.setItem(SSO_LOGIN_ERROR_KEY, "true");
    return init();
  }

  try {
    await init();
    storeAccessTokenInKeycloak(keycloak, accessToken);
    await keycloak.loadUserProfile();
    return true;
  } catch {
    clearKeycloakSession(keycloak);
    sessionStorage.setItem(SSO_LOGIN_ERROR_KEY, "true");
    return false;
  }
}
